// src/components/TrajectoryPlot.jsx
import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
  ScatterChart,
  Scatter,
  ZAxis
} from 'recharts';
import { Box, Paper, Typography, useTheme, Grid } from '@mui/material'; // Added Grid import

const TrajectoryPlot = ({ trajectory, pitchImpact }) => { // Added pitchImpact prop
  const theme = useTheme();
  
  // Prepare data for 2D plot
  const data2D = trajectory.map((point, index) => ({
    name: `Frame ${index}`,
    distance: point.x,
    height: point.y,
    speed: point.speed || 0,
    frame: index,
    timestamp: point.timestamp || 0
  }));

  // Prepare data for 3D scatter
  const data3D = trajectory.map(point => ({
    x: point.x,
    y: point.y,
    z: point.z || 0
  }));

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Ball Trajectory Analysis
      </Typography>
      
      <Grid container spacing={3}>
        {/* 2D Trajectory */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>
              2D Trajectory (Height vs Distance)
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={data2D}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="distance" 
                    label={{ value: 'Distance from crease (m)', position: 'insideBottom', offset: -5 }}
                  />
                  <YAxis 
                    label={{ value: 'Height (m)', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    formatter={(value, name) => {
                      if (name === 'height') return [`${value}m`, 'Height'];
                      if (name === 'distance') return [`${value}m`, 'Distance'];
                      if (name === 'speed') return [`${value} km/h`, 'Speed'];
                      return [value, name];
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="height"
                    stroke={theme.palette.primary.main}
                    fill={theme.palette.primary.light}
                    fillOpacity={0.3}
                    name="Ball Height"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Speed Profile */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>
              Speed Profile Over Time
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={data2D}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="timestamp" 
                    label={{ value: 'Time (seconds)', position: 'insideBottom', offset: -5 }}
                  />
                  <YAxis 
                    label={{ value: 'Speed (km/h)', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    formatter={(value) => [`${value} km/h`, 'Speed']}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="speed"
                    stroke={theme.palette.error.main}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    activeDot={{ r: 6 }}
                    name="Ball Speed"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* 3D Scatter Plot */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              3D Trajectory Path
            </Typography>
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart
                  margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    type="number" 
                    dataKey="x" 
                    name="Distance" 
                    unit="m"
                    domain={[0, 'auto']}
                  />
                  <YAxis 
                    type="number" 
                    dataKey="y" 
                    name="Height" 
                    unit="m"
                    domain={[0, 'auto']}
                  />
                  <ZAxis type="number" dataKey="z" range={[0, 100]} />
                  <Tooltip 
                    cursor={{ strokeDasharray: '3 3' }}
                    formatter={(value, name) => {
                      if (name === 'x') return [`${value}m`, 'Distance'];
                      if (name === 'y') return [`${value}m`, 'Height'];
                      if (name === 'z') return [`${value}m`, 'Lateral'];
                      return [value, name];
                    }}
                  />
                  <Legend />
                  <Scatter
                    name="Trajectory Points"
                    data={data3D}
                    fill={theme.palette.primary.main}
                    line
                    shape="circle"
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TrajectoryPlot;